import React from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, makeStyles, Theme, Typography } from '@material-ui/core';
import StorefrontIcon from '@material-ui/icons/Storefront';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import RestaurantIcon from '@material-ui/icons/Restaurant';

const useStyles = makeStyles((theme: Theme) => ({
  dialogTitle: {
    textAlign: 'center',
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(3),
  },
  dialogContent: {
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  orderTypeButton: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(2),
    textTransform: 'none',
    borderRadius: theme.spacing(1),
    justifyContent: 'flex-start',
    transition: 'all 0.2s ease',
    '&:hover': {
      opacity: 0.85,
      boxShadow: theme.shadows[3],
    },
  },
  icon: {
    fontSize: '2rem',
    flexShrink: 0,
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  buttonTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    lineHeight: 1.3,
  },
  buttonDescription: {
    fontSize: '0.8rem',
    opacity: 0.8,
    textAlign: 'left',
    marginTop: theme.spacing(0.25),
  },
}));

interface OrderTypeSelectionProps {
  /** Controls the visibility of the dialog */
  open: boolean;
  /** Callback fired when the user selects an order type */
  onSelect: (orderType: 'pickup' | 'delivery' | 'dinein') => void;
  /** Location brand primary color applied to button backgrounds */
  primaryColor?: string;
  /** Location brand secondary color applied to button text and icons */
  secondaryColor?: string;
}

const ORDER_TYPES = [
  {
    type: 'pickup' as const,
    Icon: StorefrontIcon,
    title: 'Pickup',
    description: 'Order ahead and collect in-store',
  },
  {
    type: 'delivery' as const,
    Icon: LocalShippingIcon,
    title: 'Delivery',
    description: 'Get your order delivered to your address',
  },
  {
    type: 'dinein' as const,
    Icon: RestaurantIcon,
    title: 'Dine In',
    description: 'Order for table service',
  },
];

/**
 * OrderTypeSelection – Modal dialog shown after the StartOrderCoverScreen is
 * dismissed. The customer must choose Pickup, Delivery, or Dine In before they
 * can interact with the menu.  Selection is mandatory: the dialog cannot be
 * closed without choosing an order type.
 */
const OrderTypeSelection: React.FC<OrderTypeSelectionProps> = ({ open, onSelect, primaryColor, secondaryColor }) => {
  const classes = useStyles();

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      disableScrollLock
      // Mandatory selection – prevent dismissal without choosing an order type
      disableBackdropClick
      disableEscapeKeyDown>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h5" component="h2" style={{ fontWeight: 'bold' }}>
          How would you like to order?
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.buttonContainer}>
          {ORDER_TYPES.map(({ type, Icon, title, description }) => (
            <Button
              key={type}
              className={classes.orderTypeButton}
              onClick={() => onSelect(type)}
              fullWidth
              variant="contained"
              style={{
                backgroundColor: primaryColor || undefined,
                color: secondaryColor || undefined,
              }}>
              <Icon className={classes.icon} />
              <Box className={classes.textContainer}>
                <Typography className={classes.buttonTitle}>{title}</Typography>
                <Typography className={classes.buttonDescription}>{description}</Typography>
              </Box>
            </Button>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OrderTypeSelection;
